/**
 * Wrapper for making network requests using es6 native `fetch`
 * @type class
 * @name RestClient
 */

import 'whatwg-fetch';
import 'babel-polyfill';
import get from 'lodash/get';

import Storage from './storage';
import {ACCOUNT_BLOCKED_MESSAGE, INVALID_PASSWORD_MESSAGE} from "../constants/strings";

const getToken = () => {
  try {
    const tokenData = JSON.parse(window.localStorage.getItem('tempToken'));
    if (tokenData && tokenData.access_token) {
      return tokenData.access_token;
    }
    return Storage.getItem('hubToken').access_token;
  } catch (e) {
    return null;
  }
};

// getting csrfToken from header meta tag
const getCsrfToken = () => {
  const $el = document.getElementsByName('csrf-token')[0];
  return $el ? $el.content : null;
};

export default class RestClient {
  static options = {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    // credentials: 'include', // include cookies with every request
  };

  /**
   * Parses the Object to query string
   * @param {object} obj JS object
   * @return {object} The parsed query string
   */
  static encodeToUrlParams(obj) {
    if (obj instanceof String) {
      return obj;
    }
    const queryString = Object.keys(obj)
      .reduce((a, k) => {
        a.push(`${k}=${encodeURIComponent(obj[k])}`);
        return a;
      }, []).join('&');
    return `${queryString}`;
  }


  /**
   * Parses the JSON returned by a network request
   * @param {object} response A response from a network request
   * @return {object} The parsed JSON from the request
   */
  static async responseJson(response) {
    try {
      return await response.json();
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  /**
   * Checks if a network request came back fine, and throws an error if not
   * @param {object} response   A response from a network request
   * @return {object|undefined} Returns either the response, or throws an error
   */
  static checkStatus(response, options) {
    if (response.ok) {
      return response;
    }
    const error = new Error(response.statusText);
    error.response = this.responseJson(response);
    if (response.status === 401 && !response.url.includes("/user/login")) {
      if (!(options && options.body && JSON.parse(options.body).grant_type === 'password')) {
        window.localStorage.removeItem('tempToken');
        window.localStorage.removeItem('hubToken');
        window.location = '/';
      }
    }
    throw error;
  }

  /**
   * Send HTTP requests
   * @param {String} url  API endpoint
   * @param {Object} payload
   * @return {Promise} Returns either the response in json, or throws an error
   */

  static async request(url, options) {
    const allOptions = { ...this.options, ...options };
    const combinedOptions = {
      ...allOptions,
      headers: { ...allOptions.headers, Authorization: get(options, 'headers.Authorization', `Bearer ${getToken()}`)},
    };
    const response = await fetch(url, combinedOptions);
    await this.checkStatus(response, options);
    return this.responseJson(response);
  }

  /**
   * Send HTTP GET request
   * @param {String} url  API endpoint
   * @param {Object} payload
   * @return {Promise} Returns either the response in json, or throws an error
   */

  static get(url, payload) {
    const options = { method: 'GET' };
    let endpoint = url;
    if (payload) {
      endpoint += `?${this.encodeToUrlParams(payload)}`;
    }
    return this.request(endpoint, options);
  }

  /**
   * Send HTTP POST request
   * @param {String} url  API endpoint
   * @param payload
   * @return {Promise} Returns either the response in json, or throws an error
   */
  static post(url, payload) {
    const options = { method: 'POST', body: JSON.stringify(payload) };
    return this.request(url, options);
  }

  /**
   * Send HTTP PUT request
   * @param {String} url  API endpoint
   * @param payload
   * @return {Promise} Returns either the response in json, or throws an error
   */
  static put(url, payload) {
    const options = { method: 'PUT', body: JSON.stringify(payload) };
    return this.request(url, options);
  }

  /**
   * Send HTTP POST request for form data
   * @param {String} url API endpoint
   * @param payload
   * @return {Promise} Returns either the response in json, or throws an error
   */
  static postWithFile(url, payload) {
    const options = {
      method: 'POST',
      body: payload,
      headers: {
        ...this.options.headers,
        'Content-Type': 'multipart/form-data',
      },
    };
    return this.request(url, options);
  }

  static async getClientToken(url, payload) {
    //const tokenData = await this.get(url, payload);
    //return tokenData
    return Math.floor(Math.random()*16777215).toString(16);
  }
}

window.RestClient = RestClient;
